import { X, Check, RotateCcw, Sparkles, EyeOff, Eye } from "lucide-react";

/**
 * InsightsCustomizeModal — the "choose what you want to see" panel Josh asked
 * for. Rugged industrial UI: dark charcoal + safety orange, no drag-and-drop
 * (bad for touch on site tablets in gloves), just chunky checkbox tiles that
 * are easy to tap. Persists through `useInsightsPrefs` in the parent.
 */
export default function InsightsCustomizeModal({ widgets, prefs, onClose }) {
  const enabledCount = widgets.filter((w) => prefs.isOn(w.id)).length;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 overflow-y-auto" data-testid="insights-customize-modal">
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)" }} onClick={onClose} />
      <div className="relative panel-heavy w-full max-w-3xl my-8">
        <div className="flex items-start justify-between p-5 gap-4" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center"
              style={{ background: "rgba(245,130,32,0.18)", border: "1px solid rgba(245,130,32,0.4)", color: "var(--primary)" }}
            >
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-black text-2xl md:text-3xl tracking-tight leading-none">
                Customize Dashboard
              </div>
              <div className="text-[11px] uppercase tracking-widest font-black text-muted mt-1">
                {enabledCount} / {widgets.length} widgets active · saved to this device
              </div>
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" onClick={onClose} data-testid="customize-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Quick bulk actions */}
          <div className="flex gap-2 flex-wrap">
            <button
              className="btn btn-sm btn-ghost"
              onClick={prefs.enableAll}
              data-testid="customize-enable-all"
              title="Show every widget"
            >
              <Eye className="w-3.5 h-3.5" /> Show all
            </button>
            <button
              className="btn btn-sm btn-ghost"
              onClick={prefs.disableAll}
              data-testid="customize-disable-all"
              title="Hide every widget"
            >
              <EyeOff className="w-3.5 h-3.5" /> Hide all
            </button>
            <button
              className="btn btn-sm btn-ghost ml-auto"
              onClick={prefs.reset}
              data-testid="customize-reset"
              title="Restore defaults (all on)"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          </div>

          {/* Widget tiles grouped by category */}
          {groupByCategory(widgets).map(([cat, list]) => (
            <div key={cat}>
              <div className="text-[10px] uppercase tracking-widest font-black text-muted mb-2 mt-2">
                {cat}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {list.map((w) => {
                  const on = prefs.isOn(w.id);
                  return (
                    <button
                      key={w.id}
                      type="button"
                      onClick={() => prefs.toggle(w.id)}
                      className="panel p-3.5 text-left flex gap-3 items-start transition-all"
                      style={{
                        borderColor: on ? "rgba(245,130,32,0.55)" : "var(--border)",
                        background: on ? "rgba(245,130,32,0.06)" : "transparent",
                      }}
                      data-testid={`customize-toggle-${w.id}`}
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                        style={{
                          background: on ? "var(--primary)" : "rgba(255,255,255,0.06)",
                          color: on ? "#0a0a0a" : "rgba(255,255,255,0.55)",
                          border: on ? "1px solid var(--primary)" : "1px solid var(--border)",
                        }}
                      >
                        {on ? <Check className="w-4 h-4" strokeWidth={3} /> : (w.icon ? <w.icon className="w-4 h-4" /> : null)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-sm tracking-tight">
                          {w.title}
                        </div>
                        <div className="text-[11px] text-muted mt-0.5 leading-snug">
                          {w.description}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="p-5 flex justify-end" style={{ borderTop: "1px solid var(--border)" }}>
          <button className="btn btn-primary" onClick={onClose} data-testid="customize-done">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function groupByCategory(widgets) {
  const byCat = new Map();
  widgets.forEach((w) => {
    const c = w.category || "General";
    if (!byCat.has(c)) byCat.set(c, []);
    byCat.get(c).push(w);
  });
  return [...byCat.entries()];
}
